import { uniq } from 'lodash';
import { nanoid } from 'nanoid';
import { API_WORKFLOW_BUSINESS_KEY_FOR_NON_EXTERNAL_USERS } from '../../common/env';
import { ADMIN_ROLES, CHECKER_ROLES, CLIENT_PREFIX, MAKER_ROLES } from '../../common/model';

interface UserTokenPayload {
    name: string;
    preferred_username: string;
    given_name: string;
    family_name: string;
    email: string;
    realm_access: {
        roles: string[];
    };
    businessKey?: string; // only set for external users
}

const JWT_DELIMITER = '.';

export const getUserDetails = (currentToken: string): UserTokenPayload | undefined => {
    const parts = currentToken?.split(JWT_DELIMITER);
    if (parts.length < 3) {
        return undefined;
    }
    const payload = parts[1];
    try {
        const decodedPayload: UserTokenPayload = JSON.parse(atob(payload));
        return decodedPayload;
    } catch {
        return undefined;
    }
};

export const getBusinessKeyFromToken = (currentToken: string): string => {
    const userDetails = getUserDetails(currentToken);

    if (userDetails && userDetails.businessKey) {
        return userDetails.businessKey;
    } else {
        return API_WORKFLOW_BUSINESS_KEY_FOR_NON_EXTERNAL_USERS;
    }
};

export const getUserRoles = (currentToken: string): string[] => {
    const userDetails = getUserDetails(currentToken);

    if (
        userDetails &&
        userDetails.realm_access &&
        userDetails.realm_access.roles &&
        userDetails.realm_access.roles.length > 0
    ) {
        return userDetails.realm_access.roles;
    }

    return [];
};

export const isUserAdminRole = (roles: string[]): boolean => {
    return roles.some((each) => ADMIN_ROLES.includes(each));
};

export const isUserAdmin = (currentToken: string): boolean => {
    return isUserAdminRole(getUserRoles(currentToken));
};

export const SUPPORT_CLIENT_PREFIX = 'support';
export const DEFAULT_CLIENT_ID = 'protium';
export const EXTERNAL_USER_ROLE = 'turiya-external-users';

export const getClients = (currentToken: string): string[] => {
    const internalRoles = getUserRoles(currentToken)
        .filter((each) => each.startsWith(CLIENT_PREFIX))
        .map((each) => each.substring(7));
    const externalRoles = getUserRoles(currentToken)
        .filter((each) => each.startsWith(CLIENT_PREFIX) || each.startsWith(`external_${CLIENT_PREFIX}`))
        .map((each) => each.substring(16));
    return uniq([...internalRoles, ...externalRoles]);
};

export const getNonSupportClients = (currentToken: string): string[] => {
    return getClients(currentToken).filter((each) => each !== `${CLIENT_PREFIX}${SUPPORT_CLIENT_PREFIX}`);
};

export const getFirstNonSupportClients = (currentToken: string): string => {
    const clients = getNonSupportClients(currentToken);
    return clients.length > 0 ? clients[0] : DEFAULT_CLIENT_ID;
};

export const isSupportUser = (currentToken: string): boolean => {
    return getUserRoles(currentToken).includes(`${CLIENT_PREFIX}${SUPPORT_CLIENT_PREFIX}`);
};

export const isExternalUser = (currentToken: string): boolean => {
    return getUserRoles(currentToken).includes(EXTERNAL_USER_ROLE);
};

export const isCheckerRole = (roles: string[]): boolean => {
    return roles.some((each) => CHECKER_ROLES.includes(each));
};

export const isChecker = (currentToken: string): boolean => {
    return isCheckerRole(getUserRoles(currentToken));
};

export const isMakerRole = (roles: string[]): boolean => {
    return roles.some((each) => MAKER_ROLES.includes(each));
};

export const isMaker = (currentToken: string): boolean => {
    return isMakerRole(getUserRoles(currentToken));
};

export const getUserType = (roles: string[]): 'Admin' | 'Maker' | 'Checker' => {
    if (isUserAdminRole(roles)) {
        return 'Admin';
    } else if (isCheckerRole(roles)) {
        return 'Checker';
    } else {
        return 'Maker';
    }
};

export class UniqueRenderKey {
    private key: string;

    constructor() {
        this.key = nanoid();
    }

    getKey(): string {
        return this.key;
    }

    resetKey(): void {
        this.key = nanoid();
    }
}

export const uniqueRenderKey = new UniqueRenderKey();
